import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from '@mui/material';
import { AiOutlineFileExclamation } from 'react-icons/ai';
import { BsFillPlayFill } from 'react-icons/bs';
import { RiImageAddLine } from 'react-icons/ri';
import Ribbon from 'components/ui/ribbon';
import Favorite from 'components/cards/favorite';

import { useFavorites, useClientRect } from 'hooks/app';
import { getThumbnailAssets, getCardWidthPercentage } from 'components/utils/cards';
import { sendImageError } from 'components/utils/logger';

import './styles.scss';

const CardThumbnail = ({
  alt,
  className,
  skeletonVariant,
  onLoad,
  card,
  clean,
  onClick
}) => {
  const { favorites } = useFavorites();
  const imageRef = useRef();
  const [retryImage, setRetryImage] = useState(false);
  const { rect, ref } = useClientRect();

  const { cardId, isNew, isPremiumCard, isGif, isVideo, isPhoto, isAnimation, isInfographic } = card;

  const { src, srcSet, sources, aspectRatio } = getThumbnailAssets(card, retryImage);
  const [loaded, setLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const widthPercentage = getCardWidthPercentage(aspectRatio, 'thumbnail');

  useEffect(() => {
    setLoaded(false);
    setHasError(false);
  }, [cardId]);

  useEffect(() => {
    //This is needed because onLoad is not getting called when image is loaded from cache
    if (imageRef.current && imageRef.current.complete) {
      setLoaded(true);
      setHasError(!src);
    }
  } , [imageRef.current]);

  useEffect(() => {
    if (loaded) {
      onLoad && onLoad();
    }
  }, [loaded]);

  const onImageLoad = () => {
    setTimeout(() => {
      setLoaded(true);
      setHasError(!src);
      if (!src) {
        sendImageError(null, { cardId, src });
      }
    }, 1);
  }

  const onImageError = (e) => {
    if (!retryImage) {
      setRetryImage(true);
    } else {
      setHasError(true);
      setLoaded(true);
      sendImageError(e, { cardId, src });
    }
  }

  const isFavorite = !clean && cardId && favorites ? favorites.includes(cardId) : false;

  const showNewBanner = !clean && loaded && isNew;
  const showFreeBanner = !clean && loaded && !isPremiumCard;
  const showCustomizeBanner = !clean && loaded && isPremiumCard && isPhoto;
  const showAnimationBanner = !clean && loaded && isPremiumCard && (isAnimation || isInfographic);

  const sizes = rect.width > 0 ? `${rect.width}px` : '';

  return (
    <div ref={ref} className={`card-thumbnail ${className}`} style={{ aspectRatio, width: `${widthPercentage}%` }} onClick={onClick}>
      {rect.width > 0 &&
        <div className="card-thumbnail-container" style={{ aspectRatio, height: rect.width / aspectRatio }}>
          {!loaded &&
            <Skeleton className="card-skeleton" variant={skeletonVariant}/>
          }
          <picture>
            {sources?.map((s, i) => (<source key={`thumbnail-source-${cardId}-${i}`} type={s.type} srcSet={s.srcSet} sizes={sizes}/>))}
            <img
              ref={imageRef}
              src={src}
              srcSet={srcSet}
              sizes={sizes}
              alt={alt}
              onLoad={onImageLoad}
              onError={onImageError}
            />
          </picture>
          {hasError &&
            <div className="no-src-container" style={{ aspectRatio }}>
              <AiOutlineFileExclamation className="no-src-icon"/>
            </div>
          }
          {loaded && isFavorite &&
            <Favorite className="card-thumbnail-favorite"/>
          }
          {showNewBanner && <Ribbon className="card-thumbnail-ribbon" color="red" position="left">NEW!</Ribbon>}
          {showFreeBanner && <Ribbon className={`card-thumbnail-ribbon ${showNewBanner ? 'position-2' : ''}`} color="yellow" position="left">FREE!</Ribbon>}
          {showCustomizeBanner && <Ribbon className={`card-thumbnail-ribbon wide ${showNewBanner ? 'position-2' : ''}`} color="orange" position="left">&nbsp;&nbsp;CUSTOMIZE</Ribbon>}
          {showAnimationBanner && <Ribbon className={`card-thumbnail-ribbon wide ${showNewBanner ? 'position-2' : ''}`} color="green" position="left">&nbsp;&nbsp;CUSTOMIZE</Ribbon>}
          {!clean && loaded && isGif && <div className="card-thumbnail-icon">GIF</div>}
          {!clean && loaded && isVideo && <div className="card-thumbnail-icon"><BsFillPlayFill/></div>}
          {!clean && loaded && isPhoto && <div className="card-thumbnail-icon"><RiImageAddLine/></div>}
        </div>
      }
    </div>
  );
}


CardThumbnail.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.string,
  skeletonVariant: PropTypes.string,
  onLoad: PropTypes.func,
  card: PropTypes.object.isRequired,
  clean: PropTypes.bool,
  onClick: PropTypes.func
};


CardThumbnail.defaultProps = {
  alt: '',
  className: '',
  skeletonVariant: 'rect',
  onLoad: undefined,
  clean: false,
  onClick: undefined
};

export default CardThumbnail;
